// ══════════════════════════════════════════════════════
//  projectContext.js — 项目画像统一合成层 (Step 2c)
//
//  设计目的：整合所有已收集的数据源（details / github / team / news /
//  homepage 等），通过一次 Claude 调用合成结构化"项目画像"，作为：
//   - scoreProject 评分依据（Step 2a 消费）
//   - UI 🧠 项目画像 卡片
//   - verdict.js 未来红绿旗规则（如 productMaturity=idea 自动降级）
//   - 三方 API 输出（未来）
//
//  Phase 2b: fetchHomepageContent 抓项目官网首页文本（之前 AI 只看 RootData
//  description，相当于盲人摸象）
//  Phase 2c: synthesizeProjectContext 一次 Claude 调用合成
//  成本：+1 次 fetch-html (~3s) + 1 次 Claude (~$0.02)
// ══════════════════════════════════════════════════════

import { RD_BASE } from './config';
import { compressHtml } from './teamEnhanced';

// <meta name="twitter:site"/"twitter:creator" content="@handle"> 是标准 SEO/社交分享标签，
// 纯前端渲染的 SPA（body 靠 JS 挂载，服务端拿到的原始 HTML body 是空的）也会把这段 <head>
// 服务端渲染出来——比扫 body 里的 href 更可靠。不假设 name/content 属性谁在前，先摘出整个
// <meta> 标签再分别匹配两个属性。
function extractTwitterMetaHandle(compressed) {
  const metaTags = compressed.match(/<meta\s+[^>]*>/gi) || [];
  for (const tag of metaTags) {
    if (!/name=["'](?:twitter:site|twitter:creator)["']/i.test(tag)) continue;
    const m = tag.match(/content=["']@?([a-zA-Z0-9_]{2,15})["']/i);
    if (m) return m[1];
  }
  return null;
}

// 从首页正文里找文档/博客/LinkedIn 公司页/推特链接（footer 常见位置）。
// 必须在 15000 字符截断之前的完整 compressed 文本上找——很多站点首页 compressed
// 后早就超过 15k，footer 链接会被截掉，之前 Twitter 链接发现偶发漏检就是这个原因。
// compressHtml 不会剥掉 <a href> 属性（只清 class/style/id/data-*），所以直接
// 正则 href 就能找到，不需要真正解析 DOM。
function extractHomepageLinks(compressed, baseUrl) {
  const hrefs = [...compressed.matchAll(/href\s*=\s*"([^"]+)"/gi)].map(m => m[1]);
  const find = (re) => hrefs.find(h => re.test(h)) || null;
  const resolve = (href) => {
    if (!href) return null;
    try { return new URL(href, baseUrl).href; } catch { return null; }
  };
  const twitterHref = find(/(?:twitter|x)\.com\//i);
  const twitterMetaHandle = extractTwitterMetaHandle(compressed);
  return {
    docsUrl: resolve(find(/\/(docs?|documentation)(\/|$|[?#])/i)),
    blogUrl: resolve(find(/\/blog(\/|$|[?#])/i)),
    linkedinCompanyUrl: resolve(find(/linkedin\.com\/company\//i)),
    twitterUrl: resolve(twitterHref) || (twitterMetaHandle ? `https://x.com/${twitterMetaHandle}` : null),
  };
}

// 抓项目官网首页（不止 /team 页）→ 给 synthesizer 提取产品功能 / 技术栈
// 注：proxy /api/fetch-html 已 follow 301-308 三跳，处理 www→non-www 等
export async function fetchHomepageContent(websiteUrl) {
  if (!websiteUrl) return null;
  try {
    const r = await fetch(`${RD_BASE}/api/fetch-html?url=${encodeURIComponent(websiteUrl)}`, {
      signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(15000) : undefined,
    });
    if (!r.ok) return null;
    const data = await r.json();
    if (!data?.ok || !data.html) return null;

    // 复用 teamEnhanced 的 compressHtml 剥 framer/webflow 噪音
    const compressed = compressHtml(data.html);
    if (!compressed || compressed.length < 200) return null;

    // 提取 meta description (含 og:description)
    const metaDesc = (data.html.match(/<meta[^>]+name=["']description["'][^>]+content=["']([^"']+)["']/i) ||
                     data.html.match(/<meta[^>]+property=["']og:description["'][^>]+content=["']([^"']+)["']/i) ||
                     [])[1] || '';
    const finalUrl = data.finalUrl || websiteUrl;

    return {
      finalUrl,
      contentLength:   compressed.length,
      compressedText:  compressed.slice(0, 15000), // cap 给 prompt 用
      metaDescription: metaDesc.slice(0, 300),
      links: extractHomepageLinks(compressed, finalUrl),
    };
  } catch { return null; }
}

// 把各种 input 拼成紧凑 prompt 段
function buildContextInputDigest(inputs) {
  const { details, recentUpdatesSummary, githubSignal, enhancedTeam, surfNews, homepageContent, defiData, webIntel } = inputs;

  const segs = [];

  // 1. 基础信息
  segs.push(`## 项目基础
名称：${details?.project_name || '?'}
一句话：${details?.one_liner || '?'}
描述：${(details?.description || '').slice(0, 400)}
标签：${(details?.tags || []).slice(0, 5).join(', ') || '?'}
生态：${(details?.ecosystem || []).slice(0, 3).join(', ') || '?'}
代币：${details?.token_symbol || '未发币'}
官网：${details?.social_media?.website || '?'}
GitHub：${details?.social_media?.github || details?.social_media?.code || '?'}
LinkedIn：${details?.social_media?.linkedin || '?'}`);

  // 2. 官网首页内容（如果有）—— 这是 Phase 2b 的核心
  // Q63 验证：剥掉 HTML 后 project-context-ai 仍 403，且同次 team-summary（无 HTML）也 403
  // 说明 lanyi 行为是状态/时段相关，不由 prompt 内容决定。回滚实验。
  if (homepageContent?.compressedText) {
    segs.push(`## 官网首页内容（已压缩剥噪）
Meta description: ${homepageContent.metaDescription || '无'}
正文摘录（前 4000 字符）：
${homepageContent.compressedText.slice(0, 4000)}`);
  }

  // 3. GitHub 详细统计
  if (githubSignal) {
    const weeks = githubSignal.weeklyCommits || [];
    const trend = weeks.length >= 2
      ? (weeks[weeks.length - 1] - weeks[0] > 0 ? '上升' : weeks[weeks.length - 1] - weeks[0] < 0 ? '下降' : '稳定')
      : '?';
    segs.push(`## GitHub 活跃度
仓库：${githubSignal.url || '?'}
Stars：${githubSignal.stars ?? '?'} | Forks：${githubSignal.forksCount ?? '?'}
近 4 周提交：${githubSignal.commits4w ?? 0}（趋势：${trend}）
贡献者数：${githubSignal.contributorCount ?? '?'}
首次发布：${githubSignal.firstReleaseAt ? githubSignal.firstReleaseAt.slice(0, 10) : '?'}（${githubSignal.firstReleaseTag || ''}）
最后推送：${githubSignal.lastPushed ? githubSignal.lastPushed.slice(0, 10) : '?'}
仓库创建：${githubSignal.repoCreatedAt ? githubSignal.repoCreatedAt.slice(0, 10) : '?'}
是 Fork：${githubSignal.isFork ? '是 ⚠' : '否'}`);
  }

  // 4. 团队增强 + 摘要
  if (enhancedTeam) {
    const founders = (enhancedTeam.founders || []).slice(0, 6).map(f =>
      `${f.name}(${f.role || '?'})${f.priorWork?.length ? ' 前作:' + f.priorWork.slice(0,2).join(';') : ''}`
    ).join(' | ');
    const summary = enhancedTeam.summary;
    segs.push(`## 团队增强
识别成员（${(enhancedTeam.founders || []).length} 人）：${founders || '无'}
GitHub 贡献者：${(enhancedTeam.topContributors || []).length} 人
数据源：${(enhancedTeam.sourcesUsed || []).join('+') || '无'}
${summary ? `摘要：评分 ${summary.qualityScore}/100，${summary.hasDoxxedFounders ? '实名' : '匿名'}，"${summary.oneLineSummary}"，强项=[${(summary.strengths||[]).slice(0,2).join('; ')}]，关注=[${(summary.concerns||[]).slice(0,2).join('; ')}]` : ''}`);
  }

  // 5. 项目近况（Step 1）
  if (recentUpdatesSummary?.updates?.length) {
    const events = recentUpdatesSummary.updates.slice(0, 8).map(u =>
      `[${u.date}|${u.category}|${u.sentiment}] ${u.title}（${u.aiComment || ''}）`
    ).join('\n');
    segs.push(`## 项目近况（${recentUpdatesSummary.updates.length} 条要点）
${events}
整体：${recentUpdatesSummary.oneLineSummary || ''}
主题：${(recentUpdatesSummary.keyThemes || []).join('、') || ''}`);
  }

  // 6. 链上 / DeFi 数据（如果有）
  if (defiData?.tvl) {
    segs.push(`## 链上数据
TVL：$${(defiData.tvl.current || 0).toLocaleString()}
周变化：${defiData.tvl.weekChange || 0}%
${defiData.fees ? `月费用：$${(defiData.fees.monthly || 0).toLocaleString()}` : ''}
${defiData.revenue ? `月收入：$${(defiData.revenue.monthly || 0).toLocaleString()}` : ''}`);
  }

  // 7. webIntel 已有的 techIntel（之前的 AI 分析）— 作参考
  const ti = webIntel?.techIntel;
  if (ti) {
    segs.push(`## 已有 AI 技术分析参考
产品功能：${(ti.product_features || []).slice(0, 3).join(', ') || '?'}
技术栈：${(ti.tech_stack || []).slice(0, 3).join(', ') || '?'}
差异化：${(ti.differentiators || []).slice(0, 2).join('; ') || '?'}
产品阶段：${ti.product_stage || '?'}`);
  }

  return segs.join('\n\n');
}

// 主入口：一次 Claude 调用合成结构化项目画像
export async function synthesizeProjectContext(inputs) {
  if (!inputs?.details?.project_name) return null;
  const projectName = inputs.details.project_name;
  const inputDigest = buildContextInputDigest(inputs);

  const prompt = `你是 Web3 投研分析师。基于下方所有数据，对「${projectName}」做综合"项目画像"合成。

${inputDigest}

## 任务
基于上方真实数据，输出结构化 projectContext 对象。**严格基于证据，不编造**。

## 输出字段说明

**以下带 _en 的字段全部必填**（不是可选项）：跟对应中文字段表达同样的意思（翻译，不是重新总结），供英文界面直接展示。

1. **contextSummary** ≤80 字：一句话项目画像 + **contextSummary_en**（英文翻译）
   例："Solana 隐私 L2，主网已上线 8 个月，近期推出 ZK shielded transfer，GitHub 持续高频提交"

2. **productMaturity**: { stage, evidence, evidence_en }
   - stage: idea | devnet | testnet | mainnet | scaling | mature（本身就是英文枚举值，不需要翻译）
   - evidence: ≤60 字判断依据（引用具体事件）+ evidence_en 英文翻译

3. **productFeatures**: [{name, name_en, description, description_en, maturity}]
   最多 5 个，name ≤ 20 字，description ≤ 50 字，name_en/description_en 为对应英文翻译
   maturity: live | beta | planned

4. **techStack**: [≤6 个技术词]
   例：["Solana", "ZK-SNARK", "Rust", "Anchor"]（本身多为专有名词，不需要单独的 _en 字段）

5. **recentMomentum**: { productEvents30d, summary, summary_en, keyEvents, keyEvents_en }
   - productEvents30d: 数字（近 30 天产品类事件数）
   - summary: ≤60 字 + summary_en 英文翻译
   - keyEvents: 最关键 3 个事件，每条 ≤ 30 字（带日期）+ keyEvents_en 对应英文翻译（同样 3 条，一一对应）

6. **technicalDepth**: { score, basis, basis_en }
   - score: 0-10（代码原创度 / 技术壁垒 / 协议复杂度）
   - basis: ≤80 字依据 + basis_en 英文翻译

7. **competitivePosition** ≤100 字：与同赛道对比 + **competitivePosition_en** 英文翻译

8. **evidenceTags**: ["github活跃", "团队公开", "近期产品迭代", ...]
   最多 6 个，每个 ≤ 8 字，标注真实证据强度（本身是短标签，不需要单独的 _en 字段）

9. **knownRisks**: 最多 3 条，每条 ≤ 30 字（从数据中识别的具体风险）+ **knownRisks_en** 对应英文翻译（同样条数，一一对应）

10. **sourcesUsed**: ["details", "homepage", "github", "team", "news", "defi"] 中实际用到的

11. **tokenomicsDisclosure** (Q28 新增): 从 homepage / docs / whitepaper 内容里识别
    - hasWhitepaper: true/false（输入里出现"whitepaper" / "lightpaper" / "技术文档"链接或讨论 → true）
    - allocationDisclosed: true/false（输入文本里能找到具体的分配百分比 / 类别 → true）
    - vestingDisclosed: true/false（输入里有 cliff / linear / TGE 时间表 → true）
    - source: "homepage" | "docs" | "news" | "inferred" | null（实际依据来源）
    - 完全没材料 → 返回 null 整个对象

12. **inferredAllocation** (Q28 新增): 仅当 tokenomicsDisclosure.allocationDisclosed=true 才输出
    - { team: N, vc: N, community: N, treasury: N, foundation: N, other: N }（百分比 0-100，总和 ≈ 100）
    - 例：homepage 写了"团队 20%，投资人 15%，社区 50%，国库 10%，基金会 5%"
    - 不确定 → 返回 null
    - **绝不臆测 / 编造数字**

## 关键约束
- 没有的字段返回 null 或空数组，绝不编造
- evidence 必须能在上方 input 中找到对应数据
- productMaturity 严重影响 verdict，请保守判断（无证据时 testnet 优先于 mainnet）
- inferredAllocation 必须有明确文本来源（quote 在 evidence 里），否则返回 null

## 输出 JSON
{
  "contextSummary": "...",
  "contextSummary_en": "English translation",
  "productMaturity": { "stage": "...", "evidence": "...", "evidence_en": "English translation" },
  "productFeatures": [{ "name": "...", "name_en": "...", "description": "...", "description_en": "...", "maturity": "live" }],
  "techStack": [...],
  "recentMomentum": { "productEvents30d": N, "summary": "...", "summary_en": "...", "keyEvents": [...], "keyEvents_en": [...] },
  "technicalDepth": { "score": N, "basis": "...", "basis_en": "..." },
  "competitivePosition": "...",
  "competitivePosition_en": "...",
  "evidenceTags": [...],
  "knownRisks": [...],
  "knownRisks_en": [...],
  "sourcesUsed": [...],
  "tokenomicsDisclosure": { "hasWhitepaper": false, "allocationDisclosed": false, "vestingDisclosed": false, "source": null },
  "inferredAllocation": null
}`;

  try {
    const res = await fetch(`${RD_BASE}/api/ai/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-opus-4-7',
        max_tokens: 2000,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    if (!res.ok) {
      console.warn(`[project-context-ai] ❌ HTTP ${res.status}`);
      return null;
    }
    const ai = await res.json();
    // Q60: 让静默 service 也暴露 fallback 状态，验证 "lanyi 按 prompt 大小限制" 假设
    if (ai._fallback) console.warn(`[project-context-ai] ⚠️ DeepSeek 兜底, 原因: ${ai._fallback_reason || '未知'}`);
    else console.log(`[project-context-ai] ✅ ${ai.model || 'claude'} OK`);
    const text = (ai.content || []).map(b => b?.type === 'text' ? b.text : '').join('');
    const cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
    const m = cleaned.match(/\{[\s\S]*\}/);
    if (!m) return null;
    let parsed;
    try { parsed = JSON.parse(m[0]); } catch { return null; }

    const clampScore = v => Math.max(0, Math.min(10, Math.round(Number(v) ?? 0)));
    const safeArr = (a, cap) => Array.isArray(a) ? a.slice(0, cap).filter(Boolean) : [];

    return {
      contextSummary:       String(parsed.contextSummary || '').slice(0, 120),
      contextSummary_en:    String(parsed.contextSummary_en || '').slice(0, 120),
      productMaturity:      parsed.productMaturity && typeof parsed.productMaturity === 'object'
                              ? {
                                  stage:    String(parsed.productMaturity.stage || 'unknown').slice(0, 20),
                                  evidence: String(parsed.productMaturity.evidence || '').slice(0, 100),
                                  evidence_en: String(parsed.productMaturity.evidence_en || '').slice(0, 100),
                                }
                              : null,
      productFeatures:      safeArr(parsed.productFeatures, 5).map(f => ({
                              name:        String(f?.name || '').slice(0, 40),
                              name_en:     String(f?.name_en || '').slice(0, 40),
                              description: String(f?.description || '').slice(0, 80),
                              description_en: String(f?.description_en || '').slice(0, 80),
                              maturity:    ['live', 'beta', 'planned'].includes(f?.maturity) ? f.maturity : 'live',
                            })).filter(f => f.name),
      techStack:            safeArr(parsed.techStack, 6).map(s => String(s).slice(0, 20)),
      recentMomentum:       parsed.recentMomentum && typeof parsed.recentMomentum === 'object'
                              ? {
                                  productEvents30d: Math.max(0, parseInt(parsed.recentMomentum.productEvents30d, 10) || 0),
                                  summary:          String(parsed.recentMomentum.summary || '').slice(0, 100),
                                  summary_en:       String(parsed.recentMomentum.summary_en || '').slice(0, 100),
                                  keyEvents:        safeArr(parsed.recentMomentum.keyEvents, 3).map(s => String(s).slice(0, 50)),
                                  keyEvents_en:     safeArr(parsed.recentMomentum.keyEvents_en, 3).map(s => String(s).slice(0, 50)),
                                }
                              : null,
      technicalDepth:       parsed.technicalDepth && typeof parsed.technicalDepth === 'object'
                              ? {
                                  score: clampScore(parsed.technicalDepth.score),
                                  basis: String(parsed.technicalDepth.basis || '').slice(0, 120),
                                  basis_en: String(parsed.technicalDepth.basis_en || '').slice(0, 120),
                                }
                              : null,
      competitivePosition:  String(parsed.competitivePosition || '').slice(0, 150),
      competitivePosition_en: String(parsed.competitivePosition_en || '').slice(0, 150),
      evidenceTags:         safeArr(parsed.evidenceTags, 6).map(s => String(s).slice(0, 12)),
      knownRisks:           safeArr(parsed.knownRisks, 3).map(s => {
                              // AI 偶发返回 [{risk, severity}, ...] 对象数组而非 string[]
                              if (typeof s === 'string') return s.slice(0, 50);
                              if (s && typeof s === 'object') {
                                const txt = s.risk || s.text || s.description || s.title || '';
                                return String(txt || '').slice(0, 50);
                              }
                              return String(s).slice(0, 50);
                            }).filter(s => s && s.length > 0),
      knownRisks_en:        safeArr(parsed.knownRisks_en, 3).map(s => String(s).slice(0, 50)).filter(Boolean),
      sourcesUsed:          safeArr(parsed.sourcesUsed, 8).map(s => String(s).slice(0, 20)),
      generatedAt:          new Date().toISOString(),
    };
  } catch { return null; }
}
